import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { FragmentsModels, type FragmentsModel, type SpatialTreeItem } from "@thatopen/fragments";
import fragmentsWorkerUrl from "@thatopen/fragments/worker?url";
import { DeviceLayer, type LayerDevice } from "./DeviceLayer";
import {
  DuctLayer,
  type LayerDuct,
  type LayerEquipment,
  type LayerFitting,
  type LayerTerminal,
} from "./DuctLayer";
import { ConduitLayer } from "./ConduitLayer";
import { MechanicalLayer, type LayerMechanicalEquipment } from "./MechanicalLayer";
import { MemberLayer } from "./MemberLayer";
import type { LayerConduit, LayerPullBox } from "../mep/raceways";
import type { LayerBeam, LayerColumn, LayerLateral } from "../mep/members";
import { computeOverlayOffset, type AlignmentSample } from "./align";

/**
 * Return `ids` with every entry in `hidden` removed, preserving order. Used to
 * keep decorative/hidden elements (fence + spaceless-datum geometry) out of the
 * id sets that storey isolation and "show all" would otherwise re-reveal.
 */
export function subtractHidden(ids: number[], hidden: readonly number[]): number[] {
  if (hidden.length === 0) return ids;
  const drop = new Set(hidden);
  return ids.filter((id) => !drop.has(id));
}

/** Plan-side evidence for overlay alignment (one space with a footprint). */
export interface AlignmentSpace {
  /** IFC express id — equals the Fragments local id of the same space. */
  ifcId: number;
  /** Footprint centroid, plan meters. */
  planX: number;
  planY: number;
  /** Storey elevation, meters; null when unknown. */
  elevation: number | null;
}

/**
 * Three.js WebGL2 viewer — managed entirely outside React's render cycle.
 *
 * Pattern (spec §7): instantiate once, call `mount(canvas)` to attach to a DOM
 * canvas, call `dispose()` on unmount. React never touches scene internals;
 * the page calls the imperative methods below (`loadModel`, `isolateStorey`,
 * `showAllItems`).
 *
 * BIM rendering uses ThatOpen Fragments (ADR 0001/0008): the parse worker
 * produces a Fragments binary, `loadModel` streams it through the bundled
 * Fragments worker and adds the resulting `THREE.Object3D` to the scene.
 */
export class Viewer {
  private renderer: THREE.WebGLRenderer | null = null;
  private scene: THREE.Scene;
  private camera: THREE.PerspectiveCamera;
  private controls: OrbitControls | null = null;
  private animFrameId: number | null = null;

  /** Placeholder shown until the first model loads. */
  private placeholder: THREE.Mesh | null;

  private fragments: FragmentsModels | null = null;
  private model: FragmentsModel | null = null;
  /** Storey express/local ID → all descendant item IDs (including itself). */
  private storeyItems = new Map<number, number[]>();
  /** All item IDs with geometry in the current model (for bulk hide). */
  private allItemIds: number[] = [];
  /**
   * Express/local IDs of decorative elements (chain-link fence + geometry on
   * spaceless datum levels) that must stay hidden — kept out of the storey /
   * show-all id sets and re-hidden after any `resetVisible`.
   */
  private hiddenElementIds: number[] = [];

  /** Generated MEP device markers (instanced meshes per kind). */
  private deviceLayer = new DeviceLayer();
  /** Generated HVAC ductwork (merged duct runs + fitting/terminal markers). */
  private ductLayer = new DuctLayer();
  /** Generated electrical raceways (conduit centerlines + pull-box glyphs). */
  private conduitLayer = new ConduitLayer();
  /** Generated mechanical equipment (RTU/AHU/pump/VAV glyphs). */
  private mechanicalLayer = new MechanicalLayer();
  private memberLayer = new MemberLayer();

  constructor() {
    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(0x1a1a2e);
    this.scene.add(this.deviceLayer.group);
    this.scene.add(this.ductLayer.group);
    this.scene.add(this.conduitLayer.group);
    this.scene.add(this.mechanicalLayer.group);
    this.scene.add(this.memberLayer.group);

    this.camera = new THREE.PerspectiveCamera(60, 1, 0.1, 1000);
    this.camera.position.set(0, 0, 3);

    // Placeholder spinning cube (removed when a model loads).
    const geometry = new THREE.BoxGeometry(1, 1, 1);
    const material = new THREE.MeshStandardMaterial({ color: 0x4a90d9 });
    this.placeholder = new THREE.Mesh(geometry, material);
    this.scene.add(this.placeholder);

    // Lighting
    const ambient = new THREE.AmbientLight(0xffffff, 0.4);
    const directional = new THREE.DirectionalLight(0xffffff, 1.0);
    directional.position.set(5, 10, 7.5);
    this.scene.add(ambient, directional);
  }

  /** Attach renderer to `canvas`, wire orbit controls, start the loop. */
  mount(canvas: HTMLCanvasElement): void {
    this.renderer = new THREE.WebGLRenderer({
      canvas,
      antialias: true,
      powerPreference: "high-performance",
    });

    const { clientWidth: w, clientHeight: h } = canvas;
    this.renderer.setSize(w, h, false);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();

    this.controls = new OrbitControls(this.camera, canvas);
    this.controls.enableDamping = true;
    // Fragments stream level-of-detail tiles relative to the camera: nudge an
    // update while orbiting, force-finish when the interaction ends.
    this.controls.addEventListener("change", () => {
      void this.fragments?.update().catch(() => undefined);
    });
    this.controls.addEventListener("end", () => {
      void this.fragments?.update(true).catch(() => undefined);
    });

    this.animate();
  }

  private animate = (): void => {
    this.animFrameId = requestAnimationFrame(this.animate);
    if (this.placeholder) {
      this.placeholder.rotation.x += 0.005;
      this.placeholder.rotation.y += 0.01;
    }
    this.controls?.update();
    this.renderer?.render(this.scene, this.camera);
  };

  /**
   * Remove the currently loaded 3D model (if any) from the scene and
   * release its Fragments resources, WITHOUT loading a replacement. Shared
   * by `loadModel` (about to load a new model) and `clearModel` (nothing to
   * load — e.g. a new file's 3D conversion failed).
   */
  private async disposeCurrentModel(): Promise<void> {
    if (this.model) {
      this.scene.remove(this.model.object);
      const previousId = this.model.modelId;
      this.model = null;
      this.storeyItems.clear();
      this.allItemIds = [];
      await this.fragments?.disposeModel(previousId);
    }
  }

  /**
   * Remove any currently loaded 3D model from the canvas without loading a
   * replacement. For the case where a NEW file's 3D (Fragments) conversion
   * failed but its 2D summary is still being shown: without this, the
   * previous file's mesh would keep rendering in the canvas right alongside
   * the new file's stats/panels — a stale, misleading combination. A no-op
   * if nothing is currently loaded.
   */
  async clearModel(): Promise<void> {
    await this.disposeCurrentModel();
  }

  /**
   * Load a Fragments binary (produced by the IFC parse worker), replacing any
   * previously loaded model. Fits the camera to the model bounds and indexes
   * the spatial structure for storey isolation.
   */
  async loadModel(
    fragmentsBytes: ArrayBuffer,
    modelId: string,
    hiddenElementIds: readonly number[] = [],
  ): Promise<void> {
    this.fragments ??= new FragmentsModels(fragmentsWorkerUrl);

    await this.disposeCurrentModel();
    this.hiddenElementIds = [...hiddenElementIds];
    // Any overlay alignment belonged to the previous model.
    this.deviceLayer.group.position.set(0, 0, 0);
    this.ductLayer.group.position.set(0, 0, 0);
    this.conduitLayer.group.position.set(0, 0, 0);
    this.mechanicalLayer.group.position.set(0, 0, 0);
    this.memberLayer.group.position.set(0, 0, 0);

    if (this.placeholder) {
      this.scene.remove(this.placeholder);
      this.placeholder.geometry.dispose();
      (this.placeholder.material as THREE.Material).dispose();
      this.placeholder = null;
    }

    const model = await this.fragments.load(fragmentsBytes, {
      modelId,
      camera: this.camera,
    });
    this.model = model;
    this.scene.add(model.object);
    await this.fragments.update(true);

    this.fitCameraToBox(model.box);
    await this.indexStoreys(model);
    // Hide decorative elements from the start; indexStoreys has already removed
    // them from the storey / show-all id sets so they stay hidden.
    if (this.hiddenElementIds.length > 0) {
      await model.setVisible(this.hiddenElementIds, false);
    }
    await this.fragments.update(true);
  }

  /** Build the storey → descendant-items index from the spatial structure. */
  private async indexStoreys(model: FragmentsModel): Promise<void> {
    this.storeyItems = new Map<number, number[]>();
    const tree = await model.getSpatialStructure();

    // The tree alternates category nodes (localId null, category set) with
    // instance nodes (localId set, category null): a "IFCBUILDINGSTOREY"
    // category node's children are the storey instances.
    const collectIds = (node: SpatialTreeItem, into: number[]): void => {
      if (node.localId !== null && node.localId !== undefined) {
        into.push(node.localId);
      }
      for (const child of node.children ?? []) collectIds(child, into);
    };

    const walk = (node: SpatialTreeItem): void => {
      if (node.category && /BUILDINGSTOREY/i.test(node.category)) {
        for (const instance of node.children ?? []) {
          if (instance.localId === null || instance.localId === undefined) {
            continue;
          }
          const ids: number[] = [];
          collectIds(instance, ids);
          ids.sort((a, b) => a - b);
          this.storeyItems.set(instance.localId, subtractHidden(ids, this.hiddenElementIds));
        }
        return; // storeys do not nest
      }
      for (const child of node.children ?? []) walk(child);
    };
    walk(tree);

    this.allItemIds = subtractHidden(await model.getItemsIdsWithGeometry(), this.hiddenElementIds);
  }

  /**
   * Show only the items contained in (or aggregated under) one storey.
   * `deviceStoreyId` is the server-side storey id matching the generated
   * device rows; passing it keeps the device markers in sync with the
   * isolation (omit/`null` leaves all device storeys visible).
   */
  async isolateStorey(storeyId: number, deviceStoreyId: string | null = null): Promise<void> {
    if (!this.model || !this.fragments) return;
    const ids = this.storeyItems.get(storeyId);
    if (!ids) return;
    this.deviceLayer.setStoreyFilter(deviceStoreyId);
    this.ductLayer.setStoreyFilter(deviceStoreyId);
    this.conduitLayer.setStoreyFilter(deviceStoreyId);
    this.mechanicalLayer.setStoreyFilter(deviceStoreyId);
    this.memberLayer.setStoreyFilter(deviceStoreyId);
    await this.model.setVisible(this.allItemIds, false);
    await this.model.setVisible(ids, true);
    await this.fragments.update(true);
  }

  /** Reset all visibility overrides (undo storey isolation). */
  async showAllItems(): Promise<void> {
    this.deviceLayer.setStoreyFilter(null);
    this.ductLayer.setStoreyFilter(null);
    this.conduitLayer.setStoreyFilter(null);
    this.mechanicalLayer.setStoreyFilter(null);
    this.memberLayer.setStoreyFilter(null);
    if (!this.model || !this.fragments) return;
    await this.model.resetVisible();
    // resetVisible clears every override, including our decorative-element
    // hide — re-apply it so those elements stay hidden.
    if (this.hiddenElementIds.length > 0) {
      await this.model.setVisible(this.hiddenElementIds, false);
    }
    await this.fragments.update(true);
  }

  /**
   * Align the MEP overlay layers to the loaded Fragments mesh: the plan
   * coordinates the MEP engine works in and the mesh's coordinates share
   * axes but differ by a constant per-model translation (the Fragments
   * importer recenters). Recover it from the spaces' own geometry — their
   * Fragments bounding boxes vs their footprint centroids — and apply it to
   * both layer groups. Returns true when an offset was applied.
   */
  async alignLayersToModel(spaces: readonly AlignmentSpace[]): Promise<boolean> {
    if (!this.model || spaces.length === 0) return false;
    // A handful of samples is plenty for a median; cap the box query.
    const sample = spaces.slice(0, 24);
    let boxes: THREE.Box3[];
    try {
      boxes = await this.model.getBoxes(sample.map((s) => s.ifcId));
    } catch {
      return false; // No boxes (e.g. spaces without geometry) → leave unaligned.
    }
    const samples: AlignmentSample[] = [];
    for (let i = 0; i < sample.length && i < boxes.length; i++) {
      const box = boxes[i];
      if (!box || box.isEmpty()) continue;
      samples.push({
        planX: sample[i].planX,
        planY: sample[i].planY,
        elevation: sample[i].elevation,
        box: { min: box.min, max: box.max },
      });
    }
    const offset = computeOverlayOffset(samples);
    if (!offset) return false;
    this.deviceLayer.group.position.set(offset.x, offset.y, offset.z);
    this.ductLayer.group.position.set(offset.x, offset.y, offset.z);
    this.conduitLayer.group.position.set(offset.x, offset.y, offset.z);
    this.mechanicalLayer.group.position.set(offset.x, offset.y, offset.z);
    this.memberLayer.group.position.set(offset.x, offset.y, offset.z);
    return true;
  }

  /** Replace the rendered MEP device markers (after generation). */
  setDevices(devices: readonly LayerDevice[]): void {
    this.deviceLayer.setDevices(devices);
  }

  /** Toggle one device kind's markers (layer panel). */
  setDeviceKindVisible(kind: string, visible: boolean): void {
    this.deviceLayer.setKindVisible(kind, visible);
  }

  /** Device kinds currently loaded, sorted. */
  deviceKinds(): string[] {
    return this.deviceLayer.kinds();
  }

  /** Toggle ALL fire-device markers (independent of the ducts layer). */
  setDeviceLayerVisible(visible: boolean): void {
    this.deviceLayer.group.visible = visible;
  }

  /** Replace the rendered HVAC ductwork (after generation). */
  setDucts(
    ducts: readonly LayerDuct[],
    fittings: readonly LayerFitting[] = [],
    terminals: readonly LayerTerminal[] = [],
    equipment: readonly LayerEquipment[] = [],
  ): void {
    this.ductLayer.setDucts(ducts, fittings, terminals, equipment);
  }

  /** Toggle the whole ducts layer (independent of fire-device markers). */
  setDuctLayerVisible(visible: boolean): void {
    this.ductLayer.setLayerVisible(visible);
  }

  /** Replace the rendered electrical raceways (after generation). */
  setRaceways(conduits: readonly LayerConduit[], pullBoxes: readonly LayerPullBox[] = []): void {
    this.conduitLayer.setRaceways(conduits, pullBoxes);
  }

  /** Toggle the whole raceway layer (independent of ducts/devices). */
  setConduitLayerVisible(visible: boolean): void {
    this.conduitLayer.setLayerVisible(visible);
  }

  /**
   * Replace the rendered mechanical equipment (after generation). Rows must
   * carry WORLD z — the raw mechanical Output is storey-local, so the caller
   * lifts z by the storey elevation first, exactly as the conduit path does.
   */
  setMechanicalEquipment(equipment: readonly LayerMechanicalEquipment[]): void {
    this.mechanicalLayer.setEquipment(equipment);
  }

  /** Toggle the whole mechanical layer (independent of ducts/devices/raceways). */
  setMechanicalLayerVisible(visible: boolean): void {
    this.mechanicalLayer.setLayerVisible(visible);
  }

  /** Toggle one mechanical equipment kind's glyphs (layer panel). */
  setMechanicalKindVisible(kind: LayerMechanicalEquipment["kind"], visible: boolean): void {
    this.mechanicalLayer.setKindVisible(kind, visible);
  }

  /** Mechanical equipment kinds present, sorted (deterministic for UI toggles). */
  mechanicalKinds(): LayerMechanicalEquipment["kind"][] {
    return this.mechanicalLayer.kinds();
  }

  /** Rendered mechanical marker count across all kinds (tests/UI counts). */
  mechanicalMarkerCount(): number {
    return this.mechanicalLayer.totalVisibleCount();
  }

  /** Replace the rendered structural frame (after generation). */
  setMembers(
    columns: readonly LayerColumn[],
    beams: readonly LayerBeam[],
    lateral: readonly LayerLateral[] = [],
  ): void {
    this.memberLayer.setMembers(columns, beams, lateral);
  }

  /** Toggle the whole structural member layer. */
  setMemberLayerVisible(visible: boolean): void {
    this.memberLayer.setLayerVisible(visible);
  }

  /**
   * Toggle the imported IFC shell.
   *
   * Generated overlays live INSIDE the building envelope, so an opaque shell
   * hides them. Structural makes this acute — a whole frame is enclosed — but
   * it applies to devices and ducts too, which is why this toggles the shell
   * rather than being structural-specific.
   */
  setModelVisible(visible: boolean): void {
    if (this.model) this.model.object.visible = visible;
  }

  /** Frame the camera on a bounding box (no-op for empty boxes). */
  private fitCameraToBox(box: THREE.Box3): void {
    if (box.isEmpty()) return;
    const sphere = box.getBoundingSphere(new THREE.Sphere());
    const radius = Math.max(sphere.radius, 1e-3);
    const fovRad = (this.camera.fov * Math.PI) / 180;
    const distance = (radius / Math.sin(fovRad / 2)) * 1.15;

    const direction = new THREE.Vector3(1, 0.7, 1).normalize();
    this.camera.position.copy(sphere.center).addScaledVector(direction, distance);
    this.camera.near = Math.max(distance / 1000, 0.01);
    this.camera.far = distance * 100;
    this.camera.updateProjectionMatrix();

    if (this.controls) {
      this.controls.target.copy(sphere.center);
      this.controls.update();
    }
  }

  /** Resize the renderer to match the canvas container. */
  resize(width: number, height: number): void {
    this.renderer?.setSize(width, height, false);
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
  }

  /** Tear down the renderer and release GPU/worker resources. */
  dispose(): void {
    if (this.animFrameId !== null) {
      cancelAnimationFrame(this.animFrameId);
      this.animFrameId = null;
    }
    this.controls?.dispose();
    this.controls = null;
    this.deviceLayer.dispose();
    this.ductLayer.dispose();
    this.conduitLayer.dispose();
    this.mechanicalLayer.dispose();
    this.memberLayer.dispose();
    this.model = null;
    this.storeyItems.clear();
    this.allItemIds = [];
    this.hiddenElementIds = [];
    void this.fragments?.dispose().catch(() => undefined);
    this.fragments = null;
    this.renderer?.dispose();
    this.renderer = null;
  }
}
